"use client";

import type { HeaderAnnouncementBanner } from "@lib/types";
import { AnimatePresence, motion } from "framer-motion";
import { startTransition, useEffect, useMemo, useState } from "react";

import { resolveCtaHref } from "@/components/shared/cta";
import { Container } from "@/components/ui/container";
import { iconOrNull } from "@/components/ui/icons";

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>> | null;

type AnnouncementBannerProps = {
  banner?: HeaderAnnouncementBanner | null;
  IconCmp?: IconType;
};

const CloseIconCmp = iconOrNull("X");

export default function AnnouncementBanner({ banner, IconCmp }: AnnouncementBannerProps) {
  const [shouldShow, setShouldShow] = useState<boolean | null>(null);

  const storageKey = useMemo(() => {
    if (!banner?.dismissible) return null;
    const slug = `${banner.eyebrow ?? ""}-${banner.message ?? ""}`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    return slug ? `announcementBannerDismissed:${slug}` : "announcementBannerDismissed";
  }, [banner?.dismissible, banner?.eyebrow, banner?.message]);

  useEffect(() => {
    const isEnabled = Boolean(banner?.enabled);
    const hasMessage = Boolean(banner?.message);

    if (!isEnabled || !hasMessage) {
      startTransition(() => setShouldShow(false));
      return;
    }

    if (banner?.dismissible === false || !storageKey) {
      startTransition(() => setShouldShow(true));
      return;
    }

    let nextShouldShow = true;
    try {
      const dismissed = localStorage.getItem(storageKey) === "true";
      nextShouldShow = !dismissed;
    } catch {
      nextShouldShow = true;
    }

    startTransition(() => setShouldShow(nextShouldShow));
  }, [banner?.enabled, banner?.message, banner?.dismissible, storageKey]);

  const handleDismiss = () => {
    if (banner?.dismissible === false) return;
    try {
      if (storageKey) {
        localStorage.setItem(storageKey, "true");
      }
    } catch {
      // ignore storage errors
    }
    setShouldShow(false);
  };

  if (!banner?.message) {
    return null;
  }

  const eyebrow = banner.eyebrow;
  const ctaLabel = banner.ctaLabel;
  const ctaHref = resolveCtaHref(banner.ctaHref);
  const isCtaExternal =
    ctaHref && ctaHref !== "#" ? (banner.ctaOpenInNewTab ?? /^https?:\/\//i.test(ctaHref)) : false;

  return (
    <AnimatePresence initial={false}>
      {shouldShow && (
        <motion.div
          className="relative top-0 right-0 left-0 z-60 overflow-hidden border-b border-neutral-700 bg-neutral-900"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -100 }}
          transition={{ duration: 0.3 }}
        >
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 800px 100px at center, hsl(158 100% 36% / 0.2) 0%, transparent 70%)",
            }}
          />
          <Container className="relative z-10 py-3">
            <div className="flex items-center justify-start">
              <div className="hidden items-center gap-3 md:flex">
                {IconCmp ? <IconCmp className="text-emerald-light h-4 w-4" /> : null}

                <div className="flex items-center gap-2">
                  {eyebrow ? (
                    <span className="text-emerald-light text-tiny font-bold">{eyebrow}</span>
                  ) : null}
                  <span className="text-tiny font-normal text-neutral-200">{banner.message}</span>
                  {ctaLabel && ctaHref ? (
                    <a
                      href={ctaHref}
                      target={isCtaExternal ? "_blank" : undefined}
                      rel={isCtaExternal ? "noreferrer noopener" : undefined}
                      className="text-tiny hover:text-foreground font-normal text-neutral-200 underline transition-colors"
                    >
                      {ctaLabel}
                    </a>
                  ) : null}
                </div>
              </div>

              <div className="flex items-start gap-3 md:hidden">
                {IconCmp ? (
                  <IconCmp className="text-emerald-light mt-0.5 h-4 w-4 flex-shrink-0" />
                ) : null}
                <p className="text-tiny text-left font-normal text-neutral-200">
                  {eyebrow ? <span className="text-emerald-light font-bold">{eyebrow}</span> : null}
                  {eyebrow ? " " : null}
                  {banner.message}{" "}
                  {ctaLabel && ctaHref ? (
                    <a
                      href={ctaHref}
                      target={isCtaExternal ? "_blank" : undefined}
                      rel={isCtaExternal ? "noreferrer noopener" : undefined}
                      className="hover:text-foreground text-neutral-200 underline transition-colors"
                    >
                      {ctaLabel}
                    </a>
                  ) : null}
                </p>
              </div>

              {banner.dismissible !== false ? (
                <button
                  onClick={handleDismiss}
                  className="text-foreground absolute right-6 transition-colors hover:text-neutral-300"
                  aria-label="Close banner"
                >
                  {CloseIconCmp ? <CloseIconCmp className="h-4 w-4" /> : null}
                </button>
              ) : null}
            </div>
          </Container>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
